import React from 'react';
import { Store, Warehouse, GitCompare, Check } from 'lucide-react';

export type LocationViewMode = 'shop' | 'warehouse' | 'difference';

interface LocationViewSelectorProps {
  viewMode: LocationViewMode;
  onViewModeChange: (mode: LocationViewMode) => void;
  shopLocationId: string | null;
  warehouseLocationId: string | null;
  loading?: boolean;
  className?: string;
  warehouseLabel?: string;
  shopLabel?: string;
}

export const LocationViewSelector: React.FC<LocationViewSelectorProps> = ({
  viewMode,
  onViewModeChange,
  shopLocationId,
  warehouseLocationId,
  loading = false,
  className = '',
  warehouseLabel,
  shopLabel,
}) => {
  const resolvedWarehouseLabel = warehouseLabel || 'Warehouse';
  const resolvedShopLabel = shopLabel || 'Shop';

  return (
    <div className={`bg-gradient-to-r from-slate-800 via-slate-800 to-slate-700 rounded-xl p-1.5 shadow-lg border border-slate-600/50 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        {/* Warehouse Button */}
        <button
          onClick={() => onViewModeChange('warehouse')}
          disabled={loading || !warehouseLocationId}
          title={warehouseLabel ? `Shopify: ${warehouseLabel}` : undefined}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 max-w-xs
            ${viewMode === 'warehouse'
              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
              : 'text-slate-300 hover:bg-slate-700/70 hover:text-white'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <Warehouse className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{resolvedWarehouseLabel}</span>
          {viewMode === 'warehouse' && <Check className="h-3.5 w-3.5 ml-1 flex-shrink-0" />}
        </button>

        <div className="w-px h-8 bg-slate-600/50 mx-1" />

        {/* Shop Button */}
        <button
          onClick={() => onViewModeChange('shop')}
          disabled={loading || !shopLocationId}
          title={shopLabel ? `Shopify: ${shopLabel}` : undefined}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 max-w-xs
            ${viewMode === 'shop'
              ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
              : 'text-slate-300 hover:bg-slate-700/70 hover:text-white'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <Store className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{resolvedShopLabel}</span>
          {viewMode === 'shop' && <Check className="h-3.5 w-3.5 ml-1 flex-shrink-0" />}
        </button>

        <div className="w-px h-8 bg-slate-600/50 mx-1" />

        {/* Difference Button */}
        <button
          onClick={() => onViewModeChange('difference')}
          disabled={loading || !shopLocationId || !warehouseLocationId}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
            ${viewMode === 'difference'
              ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
              : 'text-slate-300 hover:bg-slate-700/70 hover:text-white'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <GitCompare className="h-4 w-4" />
          <span>Difference</span>
          {viewMode === 'difference' && <Check className="h-3.5 w-3.5 ml-1" />}
        </button>

        {/* View Mode Indicator */}
        {viewMode === 'difference' && shopLocationId && warehouseLocationId && (
          <div className="ml-auto flex items-center gap-3 px-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 ring-2 ring-blue-500/30" />
              <span className="text-xs font-medium text-slate-300">{resolvedWarehouseLabel}</span>
            </div>
            <span className="text-slate-500">vs</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500 ring-2 ring-amber-500/30" />
              <span className="text-xs font-medium text-slate-300">{resolvedShopLabel}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
